import { AuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { ReactNode, useEffect } from 'react';

import { globalConfig } from './configuration/config';

interface Cree8AuthProviderProps {
  children: ReactNode;
}

/**
 * CREE8 OIDC provider (public client, Auth Code + PKCE).
 *
 * - authority/client_id/apiAudience come from runtime config.
 * - redirect_uri is the app origin, matched byte-for-byte by CREE8. Return
 *   routing must ride in `state`, never appended to the URI.
 * - `resource` mints the access token for the CREE8 API audience.
 * - After a successful login we strip the OAuth callback params from the URL.
 */
export const Cree8AuthProvider = ({ children }: Cree8AuthProviderProps) => {
  const { issuer, clientId, apiAudience } = globalConfig.config.cree8;

  return (
    <AuthProvider
      authority={issuer}
      client_id={clientId}
      redirect_uri={window.location.origin}
      post_logout_redirect_uri={window.location.origin}
      response_type="code"
      scope="openid offline_access email profile"
      extraQueryParams={{ resource: apiAudience }}
      automaticSilentRenew
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      onSigninCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      {children}
    </AuthProvider>
  );
};

export const useAuth = () => useOidcAuth();

/** Kick off sign-in when the user lands on `/login`. */
export const useLoginRoute = () => {
  const auth = useOidcAuth();

  useEffect(() => {
    if (window.location.pathname === '/login' && !auth.isAuthenticated && !auth.isLoading) {
      void auth.signinRedirect();
    }
  }, [auth.isAuthenticated, auth.isLoading]);
};
